import React from 'react';
import { Form } from 'react-bootstrap';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import './DashboardNav.css';

const PHONE_QUERY = '(max-width: 767.98px)';

// Arrow keys, Home and End move focus along one row of tabs; Enter or Space
// opens the focused one. Only the open tab is in the Tab order, so Tab leaves
// the row for the next row instead of stepping through every tab.
const moveFocus = (event) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
    if (!(event.key in keys)) return;
    const tabs = Array.from(
        event.currentTarget.querySelectorAll('[role="tab"]')
    );
    const at = tabs.indexOf(document.activeElement);
    const step = keys[event.key];
    let next = (at + step + tabs.length) % tabs.length;
    if (step === 'first') next = 0;
    if (step === 'last') next = tabs.length - 1;
    event.preventDefault();
    tabs[next].focus();
};

const tabButtonId = (key) => `dashboard-tab-${key}`;
const paneId = (key) => `dashboard-pane-${key}`;

/** For Tab.Container's generateChildId, so each pane is named by its tab */
export const generateChildId = (key, type) =>
    type === 'tab' ? tabButtonId(key) : paneId(key);

/**
 * Two-level navigation for a coordinator dashboard. `groups` is
 * `[{ key, label, tabs: [{ key, label }] }]`: a group with one tab is a
 * top-level tab that opens it, a group with several shows its tabs as a pill
 * row under the top row. On a phone both rows collapse into one select, so
 * the work starts within the first screen instead of under wrapped rows.
 */
function DashboardNav({ groups, activeTab, onSelect, ariaLabel }) {
    const phone = useMediaQuery(PHONE_QUERY);
    const activeGroup =
        groups.find((g) => g.tabs.some((t) => t.key === activeTab)) ||
        groups[0];

    if (phone) {
        const activeLabel = groups
            .flatMap((g) => g.tabs)
            .find((t) => t.key === activeTab)?.label;
        return (
            <div className="mb-3">
                {/* Names the open pane, which the tab buttons do on a wide screen */}
                <span id={tabButtonId(activeTab)} className="visually-hidden">
                    {activeLabel}
                </span>
                <Form.Label htmlFor="dashboard-nav-select" className="small">
                    Dashboard section
                </Form.Label>
                <Form.Select
                    id="dashboard-nav-select"
                    value={activeTab}
                    onChange={(e) => onSelect(e.target.value)}
                >
                    {groups.map((group) =>
                        group.tabs.length === 1 ? (
                            <option key={group.key} value={group.tabs[0].key}>
                                {group.tabs[0].label}
                            </option>
                        ) : (
                            <optgroup key={group.key} label={group.label}>
                                {group.tabs.map((tab) => (
                                    <option key={tab.key} value={tab.key}>
                                        {tab.label}
                                    </option>
                                ))}
                            </optgroup>
                        )
                    )}
                </Form.Select>
            </div>
        );
    }

    return (
        <div className="mb-4">
            <div
                role="tablist"
                aria-label={ariaLabel}
                onKeyDown={moveFocus}
                className="nav nav-tabs dashboard-nav-groups"
            >
                {groups.map((group) => {
                    const selected = group.key === activeGroup.key;
                    return (
                        <button
                            key={group.key}
                            id={
                                group.tabs.length === 1
                                    ? tabButtonId(group.tabs[0].key)
                                    : `dashboard-group-${group.key}`
                            }
                            type="button"
                            role="tab"
                            tabIndex={selected ? 0 : -1}
                            aria-selected={selected}
                            className={`nav-link${selected ? ' active' : ''}`}
                            onClick={() =>
                                onSelect(
                                    selected ? activeTab : group.tabs[0].key
                                )
                            }
                        >
                            {group.tabs.length === 1
                                ? group.tabs[0].label
                                : group.label}
                        </button>
                    );
                })}
            </div>
            {activeGroup.tabs.length > 1 && (
                <div
                    role="tablist"
                    aria-label={`${activeGroup.label} views`}
                    onKeyDown={moveFocus}
                    className="nav nav-pills gap-2 pt-3 dashboard-nav-views"
                >
                    {activeGroup.tabs.map((tab) => (
                        <button
                            key={tab.key}
                            id={tabButtonId(tab.key)}
                            type="button"
                            role="tab"
                            tabIndex={tab.key === activeTab ? 0 : -1}
                            aria-selected={tab.key === activeTab}
                            className={`nav-link${tab.key === activeTab ? ' active' : ''}`}
                            onClick={() => onSelect(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default DashboardNav;
